import React, { useCallback, useState, useEffect } from "react"
import { CustomPicker, type InjectedColorProps, type ColorResult } from "react-color"
import { Hue, Saturation, EditableInput } from "react-color/lib/components/common"
import ColorUtil from "@/signomatic/util/colors"

declare module "react-color" {
  export interface InjectedColorProps {
    source?: string
  }

  export interface RGBColor {
    a?: number
    b: number
    g: number
    r: number
    source?: string
  }

  export interface HexColor {
    hex: string
    source?: string
  }
}

const pointer = (() => <div className="pointer" />) as unknown as React.ComponentType<{ direction?: "vertical" }>

interface RGB {
  r: number
  g: number
  b: number
}

interface CMYK {
  c: number
  m: number
  y: number
  k: number
}

interface Hex {
  hex: string
}

type ColorChange = Hex | RGB | CMYK

const isHex = (color: ColorChange): color is Hex => {
  return (color as Hex).hex !== undefined
}

const isRGB = (color: ColorChange): color is RGB => {
  return (color as RGB).r !== undefined || (color as RGB).g !== undefined || (color as RGB).b !== undefined
}

const isCMYK = (color: ColorChange): color is CMYK => {
  return (
    (color as CMYK).c !== undefined ||
    (color as CMYK).m !== undefined ||
    (color as CMYK).y !== undefined ||
    (color as CMYK).k !== undefined
  )
}

const CMYKFromProps = (props: InjectedColorProps) => {
  const { rgb } = props

  if (!rgb) {
    return { c: 0, m: 0, y: 0, k: 0 }
  }

  const cmyk = ColorUtil.rgbToCmyk(rgb.r, rgb.g, rgb.b)

  return {
    c: Math.round(cmyk.c * 100),
    m: Math.round(cmyk.m * 100),
    y: Math.round(cmyk.y * 100),
    k: Math.round(cmyk.k * 100),
  }
}

const ChromeLikePicker: React.FC<InjectedColorProps> = props => {
  const [prevProps, setPrevProps] = useState(props)
  const [state, setState] = useState({ cmyk: CMYKFromProps(props) })

  useEffect(() => {
    if (props.rgb !== prevProps.rgb && props.source && props.source !== "cmyk") {
      setState({ cmyk: CMYKFromProps(props) })
      setPrevProps(props)
    }
  }, [prevProps, props.rgb, props.source, props])

  const handleChange = useCallback(
    (data: ColorChange) => {
      if (props.onChange) {
        if (isHex(data)) {
          props.onChange(data.hex)
        } else if (isRGB(data)) {
          props.onChange({
            r: data.r || props.rgb?.r || 0,
            g: data.g || props.rgb?.g || 0,
            b: data.b || props.rgb?.b || 0,
            source: "rgb",
          })
        } else if (isCMYK(data)) {
          const rgb = ColorUtil.cmykToRgb(
            data.c || state.cmyk.c,
            data.m || state.cmyk.m,
            data.y || state.cmyk.y,
            data.k || state.cmyk.k
          )
          setState({
            cmyk: {
              c: data.c || state.cmyk.c,
              m: data.m || state.cmyk.m,
              y: data.y || state.cmyk.y,
              k: data.k || state.cmyk.k,
            },
          })
          props.onChange({
            r: rgb.r,
            g: rgb.g,
            b: rgb.b,
            source: "cmyk",
          })
        }
      }
    },
    [props, state.cmyk.c, state.cmyk.k, state.cmyk.m, state.cmyk.y]
  )

  const noop = () => null

  const inputStyles = {
    label: {
      display: "none",
    },
  }

  return (
    <div className="custom-color-picker">
      <div className="saturation">
        <Saturation {...props} onChange={props.onChange || noop} pointer={pointer} />
      </div>
      <div className="hue">
        <Hue {...props} onChange={props.onChange || noop} pointer={pointer} />
      </div>
      <div className="inputs">
        <div className="input rgb">
          <label className="paragraph-xs bold mb-0">R</label>
          <EditableInput label="r" style={inputStyles} value={props.rgb?.r} onChange={handleChange} />
        </div>
        <div className="input rgb">
          <label className="paragraph-xs bold mb-0">G</label>
          <EditableInput label="g" style={inputStyles} value={props.rgb?.g} onChange={handleChange} />
        </div>
        <div className="input rgb">
          <label className="paragraph-xs bold mb-0">B</label>
          <EditableInput label="b" style={inputStyles} value={props.rgb?.b} onChange={handleChange} />
        </div>
        <div className="input hex">
          <label className="paragraph-xs bold mb-0">Hex</label>
          <EditableInput label="hex" style={inputStyles} value={props.hex?.replace("#", "")} onChange={handleChange} />
          <div className="hash paragraph-xs mb-0">#</div>
        </div>
      </div>
      <div className="inputs cmyk">
        <div className="input cmyk">
          <label className="paragraph-xs bold mb-0">C</label>
          <EditableInput label="c" style={inputStyles} value={state.cmyk.c} onChange={handleChange} />
        </div>
        <div className="input cmyk">
          <label className="paragraph-xs bold mb-0">M</label>
          <EditableInput label="m" style={inputStyles} value={state.cmyk.m} onChange={handleChange} />
        </div>
        <div className="input cmyk">
          <label className="paragraph-xs bold mb-0">Y</label>
          <EditableInput label="y" style={inputStyles} value={state.cmyk.y} onChange={handleChange} />
        </div>
        <div className="input cmyk">
          <label className="paragraph-xs bold mb-0">K</label>
          <EditableInput label="k" style={inputStyles} value={state.cmyk.k} onChange={handleChange} />
        </div>
      </div>
    </div>
  )
}

const CustomChromeLikePicker = CustomPicker<unknown>(ChromeLikePicker)

interface CustomColorPickerProps {
  current?: string
  onChange: (color: string) => void
  onChangeComplete: (color: string) => void
}

const CustomColorPicker: React.FC<CustomColorPickerProps> = ({ current, onChange, onChangeComplete }) => {
  return (
    <CustomChromeLikePicker
      color={current}
      onChange={(color: ColorResult) => onChange(color.hex)}
      onChangeComplete={color => onChangeComplete(color.hex)}
    />
  )
}

export default CustomColorPicker
